import { useMemo, useRef, useState } from 'react';
import { RotateCcw, TriangleAlert, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHls } from '@/hooks/useHls';
import {
  extractYouTubeId,
  isYouTubeUrl,
  type StreamVariant,
  type VideoData,
} from '@/lib/videoTypes';
import { YouTubeWarningBanner } from '@/components/YouTubeWarningBanner';
import { cn } from '@/lib/utils';

interface VideoPlayerProps {
  video: VideoData;
  autoPlay?: boolean;
}

/** Quality selector for streams with multiple imeta variants or HLS levels. */
function QualityMenu({
  variants,
  activeVariant,
  onSelectVariant,
  hlsLevels,
  hlsLevel,
  onSelectHlsLevel,
}: {
  variants: StreamVariant[];
  activeVariant: number;
  onSelectVariant: (index: number) => void;
  hlsLevels: { height: number; bitrate: number; label: string }[];
  hlsLevel: number;
  onSelectHlsLevel: (level: number) => void;
}) {
  // HLS level selection takes precedence (adaptive streams)
  if (hlsLevels.length > 1) {
    return (
      <Select
        value={String(hlsLevel)}
        onValueChange={(v) => onSelectHlsLevel(parseInt(v, 10))}
      >
        <SelectTrigger className="h-8 w-[110px] text-xs bg-black/60 border-white/20 text-white">
          <SelectValue placeholder="Quality" />
        </SelectTrigger>
        <SelectContent>
          {hlsLevels.map((level, i) => (
            <SelectItem key={i} value={String(i - 1)}>
              {level.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (variants.length > 1) {
    return (
      <Select
        value={String(activeVariant)}
        onValueChange={(v) => onSelectVariant(parseInt(v, 10))}
      >
        <SelectTrigger className="h-8 w-[110px] text-xs bg-black/60 border-white/20 text-white">
          <SelectValue placeholder="Quality" />
        </SelectTrigger>
        <SelectContent>
          {variants.map((variant, i) => (
            <SelectItem key={i} value={String(i)}>
              {variant.dimensions ? `${variant.dimensions.height}p` : variant.label !== 'Unknown' ? variant.label : `Source ${i + 1}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return null;
}

export function VideoPlayer({ video, autoPlay = true }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [variantIndex, setVariantIndex] = useState(video.streamVariants.length - 1);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [cwAccepted, setCwAccepted] = useState(false);

  const variant = video.streamVariants[variantIndex] ?? video.streamVariants[0] ?? null;
  const isYouTube = isYouTubeUrl(video.videoUrl);
  const youTubeId = isYouTube ? extractYouTubeId(video.videoUrl) : null;

  const activeVariant = variant && variant.isHls && !isYouTube ? variant : null;
  const { levels, currentLevel, setLevel } = useHls(videoRef, activeVariant, {
    retryKey,
    onFatalError: (reason) => setError(reason ?? 'Failed to load stream'),
  });

  const hasCw = !!video.contentWarning;
  const gated = hasCw && !cwAccepted;

  const poster = useMemo(() => video.thumbnailUrl, [video.thumbnailUrl]);

  if (isYouTube) {
    return (
      <div className="space-y-3">
        <YouTubeWarningBanner />
        {youTubeId && (
          <div className={cn('relative overflow-hidden rounded-xl bg-black', video.isVertical ? 'aspect-[9/16] max-h-[80vh] mx-auto' : 'aspect-video')}>
            {gated ? (
              <CwGate reason={video.contentWarning} onAccept={() => setCwAccepted(true)} />
            ) : (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${youTubeId}?rel=0&autoplay=${autoPlay ? 1 : 0}`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 h-full w-full"
              />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-hidden rounded-xl bg-black group/player', video.isVertical ? 'aspect-[9/16] max-h-[80vh] mx-auto' : 'aspect-video')}>
      {gated ? (
        <CwGate reason={video.contentWarning} onAccept={() => setCwAccepted(true)} poster={poster} />
      ) : error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
          <TriangleAlert className="h-8 w-8 text-yellow-500" />
          <p className="text-sm text-white/90 max-w-md">{error}</p>
          <Button
            variant="secondary"
            size="sm"
            className="gap-1.5"
            onClick={() => {
              setError(null);
              setRetryKey(k => k + 1);
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" /> Retry
          </Button>
        </div>
      ) : (
        <>
          <video
            key={`${variant?.url ?? 'none'}-${retryKey}`}
            ref={videoRef}
            controls
            autoPlay={autoPlay}
            playsInline
            poster={poster}
            className="h-full w-full"
            // For HLS, useHls attaches the MediaSource (or native src on
            // Safari). For direct files, <source> elements are used so the
            // browser automatically falls through to Blossom fallbacks.
            onError={(e) => {
              // Only treat it as fatal when the <video> itself errors (no
              // more sources left to try), not when a single <source> fails.
              if (e.target === videoRef.current && variant && !variant.isHls) {
                setError('Failed to load the video file. The servers may be unreachable or the format unsupported.');
              }
            }}
          >
            {variant && !variant.isHls && (
              <>
                <source src={variant.url} type={variant.mimeType} />
                {variant.fallbacks.map((fb) => (
                  <source key={fb} src={fb} type={variant.mimeType} />
                ))}
              </>
            )}
          </video>
          <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover/player:opacity-100 focus-within:opacity-100">
            <QualityMenu
              variants={video.streamVariants}
              activeVariant={variantIndex}
              onSelectVariant={(i) => {
                setError(null);
                setVariantIndex(i);
              }}
              hlsLevels={levels}
              hlsLevel={currentLevel}
              onSelectHlsLevel={setLevel}
            />
          </div>
        </>
      )}
    </div>
  );
}

/** Content warning gate shown instead of the player until accepted. */
function CwGate({ reason, onAccept, poster }: { reason?: string; onAccept: () => void; poster?: string }) {
  return (
    <button
      type="button"
      onClick={onAccept}
      className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-zinc-950/95 p-6 text-center"
      style={poster ? { backgroundImage: `url(${poster})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
    >
      <span className="absolute inset-0 bg-black/80" aria-hidden />
      <span className="relative flex flex-col items-center gap-3">
        <TriangleAlert className="h-10 w-10 text-yellow-500" />
        <span className="text-lg font-semibold text-white">Content Warning</span>
        {reason && <span className="max-w-md text-sm text-white/70">{reason}</span>}
        <span className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors">
          <Eye className="h-4 w-4" /> Show video anyway
        </span>
      </span>
    </button>
  );
}
